import { DATE_TIME_CONFIG } from "../../config";

/**
 * Returns the current date in Pacific Time (PT) timezone
 * @returns {Date} Current date in PT timezone
 */
export function today() {
  // Create a date object for the current time
  const date = new Date();

  // Get the date as a string in PT timezone
  const ptString = date.toLocaleString("en-US", {
    timeZone: "America/Los_Angeles",
  });

  // Parse that string back into a date object
  return new Date(ptString);
}

/**
 * Return how many days have passed from the start of the week if the start was a different day from Sunday
 * @param startOfWeek
 * @param dayOfWeek
 * @returns {number}
 */
export function getDaysFromStartOfWeek(startOfWeek, dayOfWeek) {
  const weekShift = dayOfWeek - startOfWeek;
  // Fix the behavior of JS computing negative modulo
  return ((weekShift % 7) + 7) % 7;
}

/**
 * Gets a new date that's shifted by nDays from the original date.
 * @param date
 * @param nDays
 * @returns {Date}
 */
export function getShiftedDate(date, nDays) {
  const newDate = new Date(date);
  newDate.setDate(date.getDate() + nDays);
  return newDate;
}

/**
 * Returns the week number for a date.
 * Uses Sunday-based week starts by default to match planning timeline behavior.
 * @param {Date} date
 * @param {number} startOfWeek
 * @returns {number}
 */
export function getWeekNumber(
  date,
  startOfWeek = DATE_TIME_CONFIG.START_OF_WEEK
) {
  const jan1 = new Date(date.getFullYear(), 0, 1);
  const days = Math.floor((date - jan1) / (24 * 60 * 60 * 1000));
  return Math.ceil((days + jan1.getDay() - startOfWeek + 1) / 7);
}

/**
 * Formats a date as "Month YYYY" (e.g. January 2026)
 * @param {Date} date
 * @param {string} locale
 * @returns {string}
 */
export function formatMonthYear(date, locale = "en-US", showYear = true) {
  return date.toLocaleDateString(locale, { month: "long", year: showYear ? "numeric" : undefined });
}

/**
 * Formats a date as "Mon D" (e.g. Jan 3)
 * @param {Date} date
 * @param {string} locale
 * @returns {string}
 */
export function formatShortMonthDay(date, locale = "en-US") {
  return date.toLocaleDateString(locale, { month: "short", day: "numeric" });
}

/**
 * Format a week-start date like the schedule timeline header ("Week of Jan 3").
 *
 * @param {Date|string|number} weekStartDate - Week start as Date or YYYY-MM-DD.
 * @param {string} [locale="en-US"]
 * @returns {string}
 */
export function formatWeekOfLabel(weekStartDate, locale = "en-US") {
  const date =
    typeof weekStartDate === "string" && !weekStartDate.includes("T")
      ? getDateInLocalTZFromDateStr(weekStartDate)
      : new Date(weekStartDate);
  return `Week of ${formatShortMonthDay(date, locale)}`;
}

/**
 * Formats a date as "Mon D, YY" (e.g. Jan 3, 25).
 *
 * @param {Date} date
 * @param {string} [locale="en-US"]
 * @returns {string}
 */
export function formatShortMonthDayYear(date, locale = "en-US") {
  return date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "2-digit",
  });
}

/**
 * Format a date like ``July 1st 2026`` (long month, ordinal day, full year).
 *
 * @param {Date|string} date - Date object or ``YYYY-MM-DD`` string.
 * @param {string} [locale="en-US"] - Locale for the month name.
 * @returns {string}
 */
export function formatOrdinalMonthDayYear(date, locale = "en-US") {
  const value =
    date instanceof Date ? date : getDateInLocalTZFromDateStr(String(date));
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return "";
  }
  const month = value.toLocaleDateString(locale, { month: "long" });
  const day = value.getDate();
  return `${month} ${day}${ordinalSuffix(day)} ${value.getFullYear()}`;
}

/**
 * Compact month + 2-digit year like ``Jul 26``.
 *
 * @param {Date|string} date - Date object or ``YYYY-MM-DD`` string.
 * @param {string} [locale="en-US"] - Locale for the month name.
 * @returns {string}
 */
export function formatShortMonthShortYear(date, locale = "en-US") {
  const value =
    date instanceof Date ? date : getDateInLocalTZFromDateStr(String(date));
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return "";
  }
  const month = value.toLocaleDateString(locale, { month: "short" });
  const year = String(value.getFullYear()).slice(-2);
  return `${month} ${year}`;
}

/**
 * Compact month + day + 2-digit year like ``Jul 1, 26``.
 *
 * @param {Date|string} date - Date object or ``YYYY-MM-DD`` string.
 * @param {string} [locale="en-US"] - Locale for the month name.
 * @returns {string}
 */
export function formatShortMonthDayShortYear(date, locale = "en-US") {
  const value =
    date instanceof Date ? date : getDateInLocalTZFromDateStr(String(date));
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return "";
  }
  const month = value.toLocaleDateString(locale, { month: "short" });
  const day = value.getDate();
  const year = String(value.getFullYear()).slice(-2);
  return `${month} ${day}, ${year}`;
}

/**
 * English ordinal suffix for a calendar day of month (1 → "st", 22 → "nd", etc.).
 * @param {number} dayOfMonth
 * @returns {string}
 */
export function ordinalSuffix(dayOfMonth) {
  const n = dayOfMonth % 100;
  if (n >= 11 && n <= 13) {
    return "th";
  }
  switch (dayOfMonth % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

/**
 * e.g. "Mon 20th April" (weekday short, ordinal day, long month; no year).
 * @param {Date} date
 * @param {string} [locale]
 * @returns {string}
 */
export function formatShortWeekdayOrdinalLongMonth(date, locale = "en-US") {
  const weekday = date.toLocaleDateString(locale, { weekday: "short" });
  const month = date.toLocaleDateString(locale, { month: "long" });
  const d = date.getDate();
  return `${weekday} ${d}${ordinalSuffix(d)} ${month}`;
}

/**
 * Returns the date of the most recent past start of the week.
 */
export function getWeekStart(
  date = today(),
  startOfWeek = DATE_TIME_CONFIG.START_OF_WEEK
) {
  // Check if already at the start
  const dayOfWeek = date.getDay();
  if (dayOfWeek === startOfWeek) {
    return new Date(date);
  }

  const daysFromStartOfWeek = getDaysFromStartOfWeek(startOfWeek, dayOfWeek);
  return getShiftedDate(date, -daysFromStartOfWeek);
}

/**
 * Returns the date of the most recent past end of the week.
 */
export function getWeekEnd(
  date,
  startOfWeek = DATE_TIME_CONFIG.START_OF_WEEK
) {
  const weekStart = getWeekStart(date, startOfWeek);
  return getShiftedDate(weekStart, 6);
}

/**
 * Returns an array of date strings, end date inclusive, for the week starting from the week start of the given input date.
 */
export function getWeekOfDates(start, end) {
  const dateDiff = end.getTime() - start.getTime();
  const fullDays = Math.floor(dateDiff / (24 * 3600_000));

  return new Array(fullDays + 1).fill(null).map((_, i) => {
    const newDate = new Date(start);
    newDate.setDate(start.getDate() + i);
    return newDate;
  });
}

/**
 * Due to dates appearing in various formats throughout the UI, this function is used to
 * switch between various string date formats.
 *
 * Date input can be a Date object or a string in the format "YYYY-MM-DD".
 */
export function formatDate(date, outputFormat) {
  if (typeof date === "string") {
    // If it's already a full datetime string (contains 'T'), parse directly
    // Otherwise use getDateInLocalTZFromDateStr for date-only strings
    date = date.includes("T") ? new Date(date) : getDateInLocalTZFromDateStr(date);
  }

  let options;
  switch (outputFormat) {
    case "day month DD, YYYY":
      options = {
        weekday: "long",
        month: "long",
        day: "2-digit",
        year: "numeric",
      };
      break;
    case "d month DD, YYYY":
      options = {
        weekday: "short",
        month: "long",
        day: "2-digit",
        year: "numeric",
      };
      break;
    case "d mon DD, YYYY":
      options = {
        weekday: "short",
        month: "short",
        day: "2-digit",
        year: "numeric",
      };
      break;
    case "d mon o DD, YYYY": {
      const weekday = date.toLocaleDateString(["en-US", "en"], {
        weekday: "short",
      });
      const month = date.toLocaleDateString(["en-US", "en"], { month: "short" });
      const d = date.getDate();
      return `${weekday}, ${d}${ordinalSuffix(d)} ${month} ${date.getFullYear()}`;
    }
    case "day month dd":
      options = {
        weekday: "short",
        month: "short",
        day: "2-digit",
      };
      break;
    case "month DD, YYYY":
      options = {
        month: "long",
        day: "2-digit",
        year: "numeric",
      };
      break;
    case "mon DD, YYYY":
      options = {
        month: "short",
        day: "2-digit",
        year: "numeric",
      };
      break;
    case "month DD":
      options = {
        month: "long",
        day: "2-digit",
      };
      break;
    case "weekday MM/DD/YY":
      options = {
        weekday: "long",
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      };
      break;
    case "weekday":
      options = { weekday: "long" };
      break;
    case "MM/DD/YY":
      options = {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      };
      break;
    case "MM/DD":
      options = {
        month: "2-digit",
        day: "2-digit",
      };
      break;
    case "YYYY-MM-DD":
      options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };
      break;
    case "YYYY.MM.DD":
      options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };
      break;
    case "MM-DD-YYYY":
      options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };
      break;
    default:
      options = {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      };
      break;
  }

  const formatter = new Intl.DateTimeFormat(["en-US", "en"], options);
  var formattedDate = formatter.format(date);

  if (outputFormat === "YYYY-MM-DD") {
    const [month, day, year] = formattedDate.split("/");
    return `${year}-${month}-${day}`;
  }
  if (outputFormat === "YYYY.MM.DD") {
    const [month, day, year] = formattedDate.split("/");
    return `${year}.${month}.${day}`;
  }
  if (outputFormat === "MM-DD-YYYY") {
    return formattedDate.split("/").join("-");
  }

  return formattedDate;
}

/**
 * Format datetime as "Oct 25, YYYY h:mm A"
 * dateString input must be in ISO-8601 format i.e. can be split by "T"
 * dateFormat must be supported by formatDate function
 * timeFormat must be either "12hr" or "24hr"
 * @param dateString
 * @param dateFormat
 * @returns {string}
 */
export const formatDateTime = (dateString, dateFormat, timeFormat = "12hr") => {
  if (!dateString) return "N/A";

  const date = new Date(dateString);
  const datePart = formatDate(dateString.split("T")[0], dateFormat);

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const formattedMinutes = minutes.toString().padStart(2, "0");

  if (timeFormat === "24hr") {
    const formattedHours = hours.toString().padStart(2, "0");
    return `${datePart} ${formattedHours}:${formattedMinutes}`;
  }

  // Default 12hr format
  const hour12 = hours % 12 || 12;
  const period = hours >= 12 ? "PM" : "AM";

  return `${datePart} ${hour12}:${formattedMinutes} ${period}`;
};

/**
 * Returns a formatted range of dates using the built-in Intl.DateTimeFormat API
 * @param start
 * @param end
 * @returns {string}
 */
export function formatDateRange(start, end) {
  const formatter = new Intl.DateTimeFormat(["en-US", "en"], {
    month: "long",
    day: "2-digit",
    year: "numeric",
  });
  return formatter.formatRange(start, end);
}

/**
 * Get a standard ISO-8601 string representation of a date
 * @param date
 * @returns {string}
 */
export function getDateString(date) {
  if (date === null) {
    return null;
  }
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const dateParts = [date.getFullYear(), month, day];
  return dateParts.join("-");
}

/**
 * Get a date object in local TZ from an ISO-8601 date string. Never use a formatted date as an input.
 * @param dateStr
 * @returns {Date}
 */
export function getDateInLocalTZFromDateStr(dateStr) {
  return new Date(dateStr + "T00:00:00.000");
}

/**
 * Converts a shortened day name (e.g., "Mon") to the full day name (e.g., "Monday").
 * @param {string} shortDay - The shortened day name (first 3 letters, case-insensitive).
 * @returns {string} The full day name.
 */
export function getFullDayName(shortDay) {
  const dayMap = {
    Mon: "Monday",
    Tue: "Tuesday",
    Wed: "Wednesday",
    Thu: "Thursday",
    Fri: "Friday",
    Sat: "Saturday",
    Sun: "Sunday",
  };

  const normalizedDay =
    shortDay.charAt(0).toUpperCase() + shortDay.slice(1).toLowerCase();
  return dayMap[normalizedDay] || ""; // Return an empty string if the input is invalid
}

/** Formats a Date object into a string in the format "YYYY:MM:DD HH:mm:ss".
 * @param {Date} date - The Date object to format.
 * @returns {string} The formatted date string.
 */
export function formatLastModifiedDate(date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Returns today's date in Pacific Time (PT) as a string in the format "YYYY-MM-DD".
 * @returns {string} Today's date in PT in "YYYY-MM-DD" format.
 */
export function getTodayInPT() {
  // Create date object in local time

  const date = new Date();

  // Get PT date string with explicit PST/PDT timezone
  const ptDateString = date.toLocaleDateString("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  // Convert MM/DD/YYYY to YYYY-MM-DD
  const [month, day, year] = ptDateString.split("/");

  return `${year}-${month}-${day}`;
}

/**
 * Returns the amount of whole weeks between two dates.
 */
export function getWholeWeeksBetween(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Set the time to the start of the day
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  // Calculate the difference in milliseconds
  const diffInMs = end - start;

  // Convert milliseconds to weeks
  const weeks = Math.floor(diffInMs / (1000 * 60 * 60 * 24 * 7));

  return weeks;
}

/**
 * Count week buckets covered by an inclusive date range (aligned to ``startOfWeek``).
 *
 * Unlike {@link getWholeWeeksBetween}, this counts distinct week-start days that
 * intersect the range (so a span that crosses a week boundary counts as 2).
 *
 * @param {Date|string} startDate - Inclusive start (``Date`` or ``YYYY-MM-DD``).
 * @param {Date|string} endDate - Inclusive end (``Date`` or ``YYYY-MM-DD``).
 * @param {number} [startOfWeek=DATE_TIME_CONFIG.START_OF_WEEK]
 * @returns {number}
 */
export function countWeeksInInclusiveRange(
  startDate,
  endDate,
  startOfWeek = DATE_TIME_CONFIG.START_OF_WEEK
) {
  const start =
    startDate instanceof Date
      ? startDate
      : getDateInLocalTZFromDateStr(String(startDate));
  const end =
    endDate instanceof Date
      ? endDate
      : getDateInLocalTZFromDateStr(String(endDate));
  let cursor = getWeekStart(start, startOfWeek);
  let count = 0;
  while (cursor <= end) {
    count += 1;
    cursor = getShiftedDate(cursor, 7);
  }
  return count;
}

/**
 * Returns a human-readable string for the number of weeks ago.
 * @param {number} wholeWeeks - The number of whole weeks ago.
 * @return {string} A string representing the number of weeks ago.
 */
export function getWeeksAgoText(wholeWeeks) {
  if (wholeWeeks === 0) {
    return "This Week";
  } else if (wholeWeeks === 1) {
    return "Last Week";
  } else if (wholeWeeks > 1) {
    return `${wholeWeeks} Weeks Ago`;
  } else if (wholeWeeks === -1) {
    return "Next Week";
  } else if (wholeWeeks < -1) {
    return `${Math.abs(wholeWeeks)} Weeks From Now`;
  }
  return "";
}

/**
 * Normalize a project/API date value to ``YYYY-MM-DD`` (or null when blank).
 *
 * Uses a string slice instead of {@link toLocalDateKey}: date-only API values
 * like ``YYYY-MM-DD`` parse as UTC midnight in ``Date`` and can shift a day in PT.
 *
 * @param {string|null|undefined} value - Raw date field from JobNumber / metrics APIs.
 * @returns {string|null}
 */
export function toProjectDateKey(value) {
  if (value == null || value === "") {
    return null;
  }
  return String(value).split("T")[0];
}

/**
 * Local calendar date as YYYY-MM-DD (uses the runtime local timezone).
 * @param {Date|string|number} dateValue
 * @returns {string}
 */
export function toLocalDateKey(dateValue) {
  const date = new Date(dateValue);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Whether `day`'s **local calendar date** falls between rangeStart and rangeEnd
 * (inclusive). Uses date keys, not timestamps, so midnight column cells match
 * spans that use {@link toLocalCalendarDay} (e.g. noon) boundaries.
 * @param {Date} day
 * @param {Date} rangeStart
 * @param {Date} rangeEnd
 * @returns {boolean}
 */
export function isDateInInclusiveRange(day, rangeStart, rangeEnd) {
  const key = toLocalDateKey(day);
  const keyStart = toLocalDateKey(rangeStart);
  const keyEnd = toLocalDateKey(rangeEnd);
  return key >= keyStart && key <= keyEnd;
}

/**
 * Number of local calendar days between two boundaries.
 * Uses {@link toLocalCalendarDay} for stable whole-day boundaries.
 *
 * @param {Date|string|number} rangeStart
 * @param {Date|string|number} rangeEnd
 * @param {boolean} [inclusiveStart=true] Whether to include `rangeStart` when counting.
 * @param {boolean} [inclusiveEnd=true] Whether to include `rangeEnd` when counting.
 * @returns {number} Negative when `rangeEnd` is before `rangeStart` (negated count from end→start).
 */
export function getCalendarDayCount(rangeStart, rangeEnd, inclusiveStart = true, inclusiveEnd = true) {
  const a = toLocalCalendarDay(rangeStart);
  const b = toLocalCalendarDay(rangeEnd);
  if (b.getTime() < a.getTime()) {
    // Preserve endpoint semantics relative to the original argument order.
    return -getCalendarDayCount(b, a, inclusiveEnd, inclusiveStart);
  }

  const start = inclusiveStart ? a : getShiftedDate(a, 1);
  const end = inclusiveEnd ? b : getShiftedDate(b, -1);
  if (end.getTime() < start.getTime()) {
    return 0;
  }

  let count = 0;
  let d = new Date(start);
  while (d.getTime() <= end.getTime()) {
    count += 1;
    d = getShiftedDate(d, 1);
  }
  return count;
}

/**
 * Coerces a value to a `Date` that represents a **single local calendar day** as a whole
 * (stable for stepping by day and for inclusive ranges). Uses a fixed clock time inside
 * that day so DST and midnight boundaries do not change the civil date.
 * @param {Date|string|number} dateValue
 * @returns {Date}
 */
export function toLocalCalendarDay(dateValue) {
  const d = new Date(dateValue);
  d.setHours(12, 0, 0, 0);
  return d;
}

/**
 * Inclusive calendar-day span from an anchor: day 1 is the anchor’s calendar day.
 * Each boundary is a {@link toLocalCalendarDay} for stable whole-day math.
 * @param {Date|string|number} anchorDate
 * @param {number} dayCount
 * @returns {{ startDate: Date, endDate: Date }}
 */
export function getInclusiveCalendarDaySpan(anchorDate, dayCount) {
  const days = Math.max(1, Math.floor(Number(dayCount)) || 1);
  const startDate = toLocalCalendarDay(anchorDate);
  const endDate = toLocalCalendarDay(getShiftedDate(startDate, days - 1));
  return { startDate, endDate };
}

/**
 * True if the local calendar day is Saturday or Sunday.
 *
 * @param {Date|string|number} dateValue
 * @returns {boolean}
 */
export function isLocalWeekend(dateValue) {
  const d = toLocalCalendarDay(dateValue);
  const day = d.getDay();
  return day === 0 || day === 6;
}

/**
 * True if the local calendar day is Monday–Friday.
 *
 * @param {Date|string|number} dateValue
 * @returns {boolean}
 */
export function isLocalWeekday(dateValue) {
  return !isLocalWeekend(dateValue);
}

/**
 * Number of Monday–Friday days between two local calendar days.
 *
 * @param {Date|string|number} rangeStart
 * @param {Date|string|number} rangeEnd
 * @param {boolean} [inclusiveStart=true] Whether to include `rangeStart` when counting.
 * @param {boolean} [inclusiveEnd=true] Whether to include `rangeEnd` when counting.
 * @returns {number} Negative when `rangeEnd` is before `rangeStart` (negated count from end→start).
 */
export function countLocalWeekdays(rangeStart, rangeEnd, inclusiveStart = true, inclusiveEnd = true) {
  const a = toLocalCalendarDay(rangeStart);
  const b = toLocalCalendarDay(rangeEnd);
  if (b.getTime() < a.getTime()) {
    // Preserve endpoint semantics relative to the original argument order.
    return -countLocalWeekdays(b, a, inclusiveEnd, inclusiveStart);
  }

  const start = inclusiveStart ? a : getShiftedDate(a, 1);
  const end = inclusiveEnd ? b : getShiftedDate(b, -1);
  if (end.getTime() < start.getTime()) {
    return 0;
  }

  let n = 0;
  let d = new Date(start);
  while (d.getTime() <= end.getTime()) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) {
      n += 1;
    }
    d = getShiftedDate(d, 1);
  }
  return n;
}

/**
 * Inclusive span where the anchor is day 1 and `weekdayCount` counts only Mon–Fri in
 * [startDate, endDate] (local calendar). Weekends extend the calendar end without
 * increasing the weekday count (e.g. Wed + 5 weekdays ends the following Tue).
 *
 * @param {Date|string|number} anchorDate
 * @param {number} weekdayCount
 * @returns {{ startDate: Date, endDate: Date }}
 */
export function getInclusiveWorkingDaySpan(anchorDate, weekdayCount) {
  const n = Math.max(1, Math.floor(Number(weekdayCount)) || 1);
  const startDate = toLocalCalendarDay(anchorDate);
  let endDate = new Date(startDate);
  while (countLocalWeekdays(startDate, endDate, true) < n) {
    endDate = getShiftedDate(endDate, 1);
  }
  return { startDate, endDate: toLocalCalendarDay(endDate) };
}

/**
 * Number of working, calendar, or both days between two dates.
 * @param {Date|string|number} startDate
 * @param {Date|string|number} endDate
 * @param {boolean} inclusive - count inclusive of start and end dates
 * @param {string} type - "working", "calendar" or "both"
 * @returns {number}
 */
export function getDayCount(startDate, endDate, inclusiveStart = false, inclusiveEnd = false, type = "both") {
  const a = toLocalCalendarDay(startDate);
  const b = toLocalCalendarDay(endDate);
  if (type === "working") {
    return countLocalWeekdays(a, b, inclusiveStart, inclusiveEnd);
  } else if (type === "calendar") {
    return getCalendarDayCount(a, b, inclusiveStart, inclusiveEnd);
  } else if (type === "both") {
    return getCalendarDayCount(a, b, inclusiveStart, inclusiveEnd);
  } else {
    throw new Error(`Invalid type: ${type}`);
  }
}

/** Returns a human-readable string for the number of calandar, working, or both days between two dates. */
export function getDayCountText(startDate, endDate, inclusiveStart = false, inclusiveEnd = false, type = "both") {
  const a = toLocalCalendarDay(startDate);
  const b = toLocalCalendarDay(endDate);

  if (a.getTime() === b.getTime()) {
    return "No changes to days";
  }

  const dayCount = getDayCount(a, b, inclusiveStart, inclusiveEnd, type);
  if (type === "both") {
    const calendarDayCount = getDayCount(a, b, inclusiveStart, inclusiveEnd, "calendar");
    const workingDayCount = getDayCount(a, b, inclusiveStart, inclusiveEnd, "working");
    const calendarAbsCount = Math.abs(calendarDayCount);
    const workingAbsCount = Math.abs(workingDayCount);
    const calendarText = `${calendarAbsCount} calendar day${calendarAbsCount === 1 ? "" : "s"}`;
    const workingText = `${workingAbsCount} working day${workingAbsCount === 1 ? "" : "s"}`;

    if (calendarDayCount === 0) {
      const direction = workingDayCount > 0 ? "added" : "removed";
      return `${workingText} ${direction}`;
    }

    if (workingDayCount === 0) {
      const direction = calendarDayCount > 0 ? "added" : "removed";
      return `${calendarText} ${direction}`;
    }

    if (calendarDayCount === workingDayCount) {
      const direction = workingDayCount > 0 ? "added" : "removed";
      return `${workingText} ${direction}`;
    }

    const direction = calendarDayCount > 0 ? "added" : "removed";
    return `${calendarText} (${workingText}) ${direction}`;
  } else if (type === "working") {
    return dayCount > 0
      ? dayCount === 1
        ? "1 working day added"
        : `${dayCount} working days added`
      : dayCount === -1
        ? "1 working day removed"
        : `${Math.abs(dayCount)} working days removed`;
  } else if (type === "calendar") {
    return dayCount > 0
      ? dayCount === 1
        ? "1 calendar day added"
        : `${dayCount} calendar days added`
      : dayCount === -1
        ? "1 calendar day removed"
        : `${Math.abs(dayCount)} calendar days removed`;
  }
  return "";
}
